import React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { TimeSelectorProps } from "../../../interfaces";

const MINUTE_STEP = 5 as const;
const HOURS_IN_HALF_DAY = 12 as const;
const MINUTES_IN_HOUR = 60 as const;

/** Wraps a value into `[0, size)` so stepping past either end rolls over. */
const wrap = (value: number, size: number): number =>
  ((value % size) + size) % size;

/** Pads to the two digits the fields always show. */
const pad = (value: number): string => String(value).padStart(2, "0");

/**
 * Hour, minute and meridiem controls. Hours are held in 24h format because that
 * is what the stored value speaks; only the display is 12h.
 */
export const TimeSelector: React.FC<TimeSelectorProps> = (props) => {
  const { hours, minutes, onChange } = props;

  const isPm: boolean = hours >= HOURS_IN_HALF_DAY;
  const displayHours: number = hours % HOURS_IN_HALF_DAY || HOURS_IN_HALF_DAY;

  const handleHoursStep = (step: number): void =>
    onChange(wrap(hours + step, HOURS_IN_HALF_DAY * 2), minutes);

  const handleMinutesStep = (step: number): void => {
    const nextMinutes: number = wrap(minutes + step, MINUTES_IN_HOUR);
    // Stepping past either end of the minutes moves the hour with it, the way
    // the native control behaved.
    const hourCarry: number =
      minutes + step >= MINUTES_IN_HOUR ? 1 : minutes + step < 0 ? -1 : 0;
    onChange(wrap(hours + hourCarry, HOURS_IN_HALF_DAY * 2), nextMinutes);
  };

  const handleHoursInput = (rawValue: string): void => {
    const parsedValue: number = Number(rawValue);
    if (!Number.isInteger(parsedValue) || parsedValue < 1 || parsedValue > 12) {
      return;
    }
    const baseHours: number = parsedValue % HOURS_IN_HALF_DAY;
    onChange(isPm ? baseHours + HOURS_IN_HALF_DAY : baseHours, minutes);
  };

  const handleMinutesInput = (rawValue: string): void => {
    const parsedValue: number = Number(rawValue);
    if (!Number.isInteger(parsedValue) || parsedValue < 0 || parsedValue > 59) {
      return;
    }
    onChange(hours, parsedValue);
  };

  const handleMeridiemChange = (nextIsPm: boolean): void => {
    if (nextIsPm === isPm) {
      return;
    }
    onChange(
      nextIsPm ? hours + HOURS_IN_HALF_DAY : hours - HOURS_IN_HALF_DAY,
      minutes,
    );
  };

  const renderStepper = (
    label: string,
    displayValue: number,
    onStep: (step: number) => void,
    onInput: (rawValue: string) => void,
  ) => (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        aria-label={`Aumentar ${label}`}
        className="flex h-5 w-9 items-center justify-center rounded text-ink-muted hover:bg-surface-sunken hover:text-ink"
        onClick={() => onStep(label === "minutos" ? MINUTE_STEP : 1)}
      >
        <ChevronUp className="h-3.5 w-3.5 shrink-0" />
      </button>
      <input
        aria-label={label}
        inputMode="numeric"
        className="h-9 w-12 rounded-md border border-line bg-surface text-center text-sm font-semibold text-ink-strong focus:border-primary focus:outline-none"
        value={pad(displayValue)}
        onChange={(event) => onInput(event.target.value)}
      />
      <button
        type="button"
        aria-label={`Disminuir ${label}`}
        className="flex h-5 w-9 items-center justify-center rounded text-ink-muted hover:bg-surface-sunken hover:text-ink"
        onClick={() => onStep(label === "minutos" ? -MINUTE_STEP : -1)}
      >
        <ChevronDown className="h-3.5 w-3.5 shrink-0" />
      </button>
    </div>
  );

  return (
    <div className="flex items-center justify-center gap-3">
      {renderStepper("horas", displayHours, handleHoursStep, handleHoursInput)}
      <span className="pb-1 text-lg font-semibold text-ink-muted">:</span>
      {renderStepper("minutos", minutes, handleMinutesStep, handleMinutesInput)}

      <div className="ml-1 flex flex-col overflow-hidden rounded-md border border-line">
        <button
          type="button"
          aria-pressed={!isPm}
          className={`h-[26px] w-12 text-xs font-semibold transition-colors ${
            isPm ? "text-ink-muted hover:bg-surface-sunken" : "bg-primary text-white"
          }`}
          onClick={() => handleMeridiemChange(false)}
        >
          AM
        </button>
        <button
          type="button"
          aria-pressed={isPm}
          className={`h-[26px] w-12 border-t border-line text-xs font-semibold transition-colors ${
            isPm ? "bg-primary text-white" : "text-ink-muted hover:bg-surface-sunken"
          }`}
          onClick={() => handleMeridiemChange(true)}
        >
          PM
        </button>
      </div>
    </div>
  );
};
